'use client'

import { Button, Textarea } from '@sarkiai/ui'
import { Square, ArrowUp, Bot } from "lucide-react"
import { useTranslations } from '@/components/I18nProvider'
import { forwardRef, useCallback } from 'react'
import { AudioRecorder } from "./AudioRecorder"

interface ChatInputProps {
  input: string
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  handleSubmit: (e?: React.FormEvent) => void
  status: string
  stop: () => void
  onFocus?: () => void
  onBlur?: () => void
  onClose?: () => void
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  function ChatInput({ input, handleInputChange, handleSubmit, status, stop, onFocus, onBlur, onClose }, ref) {
    const t = useTranslations('chat')

    const handleRecordingComplete = useCallback(async (blob: Blob) => {
      const formData = new FormData()
      formData.append('file', blob, 'voice.webm')
      // formData.append('lang', 'fr') // Could add locale here if needed

      try {
        const response = await fetch('/api/ai/voice-to-text', {
          method: 'POST',
          body: formData,
        })

        const data = await response.json()

        if (data && data.text) {
          const syntheticEvent = {
            target: {
              value: data.text
            }
          } as React.ChangeEvent<HTMLTextAreaElement>
          handleInputChange(syntheticEvent)
        }
      } catch (error) {
        console.error("Audio transcription error:", error)
      }
    }, [handleInputChange])

    return (
      <div>
        <form onSubmit={handleSubmit} className="relative">
          <div className="bg-background/95 backdrop-blur-lg supports-[backdrop-filter]:bg-background/90 border-2 border-primary rounded-4xl shadow-2xl overflow-hidden">
            <div className="px-2 py-0.5 flex items-center gap-2">
              <div
                className="relative shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                aria-hidden="true"
                onClick={onClose}
              >
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-primary" aria-hidden="true" />
                </div>
              </div>

              <Textarea
                ref={ref}
                value={input}
                onChange={handleInputChange}
                placeholder={t('placeholder') || 'Posez-moi une question...'}
                disabled={status === 'submitted' || status === 'streaming'}
                rows={1}
                className="min-h-[44px] flex-1 resize-none border-0 !bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground py-3 self-center text-sm sm:text-base"
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
                onFocus={onFocus}
                onBlur={onBlur}
              />

              <AudioRecorder
                isLoading={status === 'submitted'}
                isStreaming={status === 'streaming'}
                onRecorded={handleRecordingComplete}
                disabled={status === 'submitted' || status === 'streaming'}
              />

              <Button
                type={status === 'submitted' || status === 'streaming' ? 'button' : 'submit'}
                size="icon"
                disabled={!input?.trim() && !(status === 'submitted' || status === 'streaming')}
                onClick={(e) => {
                  if (status === 'submitted' || status === 'streaming') {
                    e.preventDefault()
                    stop()
                  }
                }}
                className="h-9 w-9 rounded-full bg-primary hover:bg-primary/90 shadow-lg shrink-0"
              >
                {status === 'submitted' || status === 'streaming' ? (
                  <Square className="h-4 w-4 fill-current" aria-hidden="true" />
                ) : (
                  <ArrowUp className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </form>

        <div className="text-center -mt-1">
          <div className="inline-block px-3 py-0.5 bg-background/50 backdrop-blur-md">
            <p className="text-[10px] text-muted-foreground/80 lowercase tracking-tight">
              {t('powered_by')}
            </p>
          </div>
        </div>
      </div>
    )
  }
)
